import React from 'react';
import { C } from '../constants';

/**
 * ESTUDIO FOTOVOLTAICO — su propia superficie, separada del cotizador gratuito.
 *
 * Dirección del dueño (2026-08-30): «el cotizador gratuito es una cosa, el estudio fotovoltaico
 * otra». El estudio es el producto **PAGO** y **el documento de construcción que se le entrega al
 * técnico para instalar** — memoria de cálculo, despiece de materiales, RETIE. Hasta acá abría el
 * MISMO wizard del cotizador (solo cambiaba un badge), así que se leía como «el cotizador otra vez».
 *
 * 🔑 Y el flujo que pidió el dueño: **el estudio NO arranca en blanco.** Los datos que el cliente
 * generó en el cotizador gratuito —tipo de techo, topología/acometida, operador de red, consumo,
 * análisis de Google Solar— se PORTAN al estudio para que el técnico AJUSTE el diseño con la visita
 * al sitio, no lo rehaga. Por eso «Continuar desde una cotización» es la vía principal.
 *
 * No reimplementa nada: ORQUESTA las piezas que ya existen —
 *   · `lector`  = `<EstudioFlux/>`: LEE el estudio canónico por su handle (`fx:`/`sh:`). La vía por
 *                 la que un estudio se ve IGUAL en las dos plataformas: una sola fuente, no dos copias.
 *   · `constructor(semilla)` = `<Quoter alto semilla={…}/>`: el motor de detalle (8760 h). Recibe la
 *                 ENTRADA de una cotización para arrancar desde ahí, o `null` para empezar de cero.
 */

const CAJA = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 18 };

const ENTREGABLES = [
  ['Memoria de cálculo', 'Memoria Técnica Eléctrica firmable — RETIE · NTC 2050 · CREG 174/2021, para radicar ante el operador de red.'],
  ['Despiece de materiales', 'La lista de compra para construir: rieles, clamps, cable por calibre, protecciones, puesta a tierra — con su certificación RETIE.'],
  ['Simulación 8760 h', 'Producción hora a hora sobre TMY real, cascada de pérdidas por etapa, y P50/P75/P90 — no un promedio.'],
  ['Ajuste con la visita', 'Parte de la cotización del cliente (techo, topología, operador, consumo) y se refina con la visita al sitio de instalación.'],
];

function Entregable({ titulo, texto }) {
  return (
    <div style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: C.heading, marginBottom: 4 }}>{titulo}</div>
      <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>{texto}</div>
    </div>
  );
}

const fmtKwp = (q) => {
  const k = q?.results?.actKwp ?? q?.results?.kwp ?? q?.entrada?.availableArea ?? null;
  return k ? `${Number(k).toFixed(1)} kWp` : null;
};

/** Fila de una cotización del cliente que puede convertirse en estudio. */
function FilaCotizacion({ q, onElegir }) {
  const kwp = fmtKwp(q);
  const techo = q?.entrada?.roofMaterial;
  const acom = q?.entrada?.acometida;
  return (
    <button type="button" onClick={() => onElegir(q)}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%', textAlign: 'left', background: C.card2, border: `1px solid ${C.border}`, borderRadius: 10, padding: '11px 14px', cursor: 'pointer' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.heading, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {q.name || q.email || `Cotización ${q.id}`}
        </div>
        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
          {[q.city || q.dept, q.operator, kwp, techo, acom].filter(Boolean).join(' · ') || 'sin detalle'}
          {q.date ? ` · ${q.date}` : ''}
        </div>
      </div>
      <span style={{ fontSize: 12, fontWeight: 800, color: C.teal, flex: '0 0 auto' }}>Abrir como estudio →</span>
    </button>
  );
}

export default function Estudio({ fxHandle = '', lector = null, constructor = null, quotes = [] }) {
  // Con handle en la URL, directo al lector. Si no, la portada con las vías.
  const [modo, setModo] = React.useState(fxHandle ? 'ver' : 'inicio');
  const [semilla, setSemilla] = React.useState(null);

  const listas = Array.isArray(quotes) ? quotes : [];
  const construir = (s) => { setSemilla(s || null); setModo('generar'); };

  if (modo === 'ver') {
    return (
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '12px 16px 40px' }}>
        {!fxHandle && (
          <button type="button" onClick={() => setModo('inicio')}
            style={{ background: 'transparent', color: C.teal, border: `1px solid ${C.teal}`, borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', marginBottom: 12 }}>
            ← Estudio fotovoltaico
          </button>
        )}
        {lector}
      </div>
    );
  }

  if (modo === 'generar') {
    return (
      <div>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '12px 16px 0', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => setModo('inicio')}
            style={{ background: 'transparent', color: C.teal, border: `1px solid ${C.teal}`, borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            ← Estudio fotovoltaico
          </button>
          {semilla && (
            <span style={{ fontSize: 11.5, color: C.muted }}>
              Partiendo de la cotización del cliente — ajústala con la visita al sitio.
            </span>
          )}
        </div>
        {typeof constructor === 'function' ? constructor(semilla) : constructor}
      </div>
    );
  }

  if (modo === 'desde') {
    return (
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '18px 16px 44px' }}>
        <button type="button" onClick={() => setModo('inicio')}
          style={{ background: 'transparent', color: C.teal, border: `1px solid ${C.teal}`, borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', marginBottom: 14 }}>
          ← Estudio fotovoltaico
        </button>
        <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 800, color: C.heading }}>Continuar desde una cotización</h2>
        <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.55, margin: '0 0 16px' }}>
          El estudio parte de lo que el cliente ya generó —techo, topología, operador, consumo— y lo
          refinás con la visita al sitio. No hay que rehacer nada.
        </p>
        {listas.length === 0 ? (
          <div style={{ ...CAJA, borderColor: C.amber }}>
            <strong style={{ color: C.heading }}>Todavía no hay cotizaciones para convertir.</strong>
            <p style={{ fontSize: 12.5, color: C.muted, margin: '6px 0 0', lineHeight: 1.5 }}>
              Cuando un cliente complete el cotizador gratuito, su estudio aparece acá para continuarlo.
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {listas.map((q) => <FilaCotizacion key={q.id} q={q} onElegir={(qq) => construir(qq.entrada || null)} />)}
          </div>
        )}
      </div>
    );
  }

  // ── Portada ──────────────────────────────────────────────────────────────────────────────────
  const hayCotizaciones = listas.length > 0;
  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '18px 16px 44px' }}>
      <div style={{ borderLeft: `3px solid ${C.orange}`, paddingLeft: 14, marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.heading }}>Estudio fotovoltaico</h1>
          <span style={{ fontSize: 10, fontWeight: 800, color: C.orange, background: `${C.orange}18`, border: `1px solid ${C.orange}44`, borderRadius: 5, padding: '3px 9px', letterSpacing: 0.6 }}>
            INGENIERÍA DE DETALLE · PAGO
          </span>
        </div>
        <p style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.6, margin: '8px 0 0' }}>
          No es la cotización rápida. Es el <strong style={{ color: C.heading }}>documento de construcción</strong> que
          se le entrega al técnico para instalar: memoria de cálculo, despiece de materiales y RETIE.
          Parte de la cotización del cliente y se ajusta con la <strong style={{ color: C.heading }}>visita al sitio</strong>.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 10, margin: '16px 0 18px' }}>
        {ENTREGABLES.map(([t, x]) => <Entregable key={t} titulo={t} texto={x} />)}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 14 }}>
        {/* CONTINUAR DESDE UNA COTIZACIÓN: la vía principal — el estudio hereda el dato del cliente. */}
        <div style={{ ...CAJA, borderLeft: `3px solid ${C.orange}`, display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 800, color: C.heading }}>Continuar desde una cotización</h3>
          <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.55, flex: 1, margin: '0 0 14px' }}>
            Toma lo que el cliente ya generó —techo, topología, operador, consumo— y lo ajustás con la
            visita. {hayCotizaciones ? `Hay ${listas.length} para continuar.` : 'Aún no hay cotizaciones.'}
          </p>
          <button type="button" onClick={() => setModo('desde')}
            style={{ background: C.orange, color: '#1a1205', border: 'none', borderRadius: 9, padding: '11px 16px', fontWeight: 800, fontSize: 13.5, cursor: 'pointer' }}>
            Elegir cotización →
          </button>
        </div>

        {/* GENERAR DESDE CERO. */}
        <div style={{ ...CAJA, display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 800, color: C.heading }}>Generar desde cero</h3>
          <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.55, flex: 1, margin: '0 0 14px' }}>
            Arranca por el <strong style={{ color: C.heading }}>recibo de 6 meses</strong> y el motor
            de 8760 h. No es el wizard de cotización rápida (tipo → contacto → transporte).
          </p>
          <button type="button" onClick={() => construir(null)}
            style={{ background: 'transparent', color: C.heading, border: `1px solid ${C.border}`, borderRadius: 9, padding: '11px 16px', fontWeight: 800, fontSize: 13.5, cursor: 'pointer' }}>
            Empezar por el recibo →
          </button>
        </div>

        {/* DESDE TELEMETRÍA: el lector del estudio canónico de FluxAI, que se arma sobre el consumo
            MEDIDO del medidor (medido-vs-simulado). Es la vía por la que se ve igual en las dos
            plataformas — una sola fuente. El perfil de carga REAL nativo (`internal/suite/consumo`)
            está bloqueado por consentimiento (decisión del dueño, PLAN.md §5.0-bis); hasta entonces
            la telemetría entra por el estudio que FluxAI ya guardó, con su handle. */}
        <div style={{ ...CAJA, borderLeft: `3px solid ${C.teal}`, display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 800, color: C.heading }}>Desde telemetría (medidor)</h3>
          <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.55, flex: 1, margin: '0 0 14px' }}>
            Abre el estudio armado sobre el <strong style={{ color: C.heading }}>consumo real medido</strong> del
            medidor (medido-vs-simulado, FluxAI), por su identificador. Se muestra <strong style={{ color: C.heading }}>en
            vivo</strong>: siempre la última versión, una sola fuente para las dos plataformas.
          </p>
          <button type="button" onClick={() => setModo('ver')}
            style={{ background: 'transparent', color: C.teal, border: `1px solid ${C.teal}`, borderRadius: 9, padding: '11px 16px', fontWeight: 800, fontSize: 13.5, cursor: 'pointer' }}>
            Abrir desde telemetría →
          </button>
        </div>
      </div>
    </div>
  );
}
